import type { ReactNode } from 'react';
import { Question } from '@phosphor-icons/react';

/**
 * KidHint — bandeau d'explication en haut de chaque drill (MVP2.5 T7, bug A8).
 *
 * Spec : `docs/WAKEUP_MVP2.5.md §2 T7`. Léon, 11 ans, n'a pas toujours le
 * contexte de POURQUOI un drill est utile. On affiche en haut du drill un
 * bandeau compact :
 *
 *   « Pourquoi ce drill ?
 *     Tu vas mémoriser les premiers coups de Caro-Kann pour ne plus jamais
 *     hésiter en début de partie. »
 *
 * Choix design (DESIGN.md §6) :
 * - Voix directe, technique mais accessible, tutoiement.
 * - Pas d'emoji caractère — uniquement Phosphor `Question` (icon, pas emoji).
 * - Couleur du module (tone) — violet (Opening), cyan (Tactics),
 *   amber (Endgame), coral (Autopsy), acid (Mission Control).
 * - Pas de dismiss : le hint doit rester visible (Léon revient régulièrement
 *   dans le drill, le rappel a une valeur à chaque session).
 *
 * Anti-cibles WAKEUP §9 : pas de gamification, pas de badge "lu/non lu".
 */

export type KidHintTone = 'violet' | 'cyan' | 'amber' | 'coral' | 'acid';

interface KidHintProps {
  tone: KidHintTone;
  title: string;
  children: ReactNode;
  className?: string;
}

const TONE_CLASS: Record<KidHintTone, { border: string; text: string; bg: string; iconBg: string }> = {
  violet: {
    border: 'border-violet-500/40',
    text: 'text-violet-500',
    bg: 'bg-violet-500/5',
    iconBg: 'bg-violet-500/15',
  },
  cyan: {
    border: 'border-cyan-500/40',
    text: 'text-cyan-500',
    bg: 'bg-cyan-500/5',
    iconBg: 'bg-cyan-500/15',
  },
  amber: {
    border: 'border-amber-500/40',
    text: 'text-amber-500',
    bg: 'bg-amber-500/5',
    iconBg: 'bg-amber-500/15',
  },
  coral: {
    border: 'border-coral-500/40',
    text: 'text-coral-500',
    bg: 'bg-coral-500/5',
    iconBg: 'bg-coral-500/15',
  },
  acid: {
    border: 'border-acid-500/40',
    text: 'text-acid-500',
    bg: 'bg-acid-500/5',
    iconBg: 'bg-acid-500/15',
  },
};

export function KidHint({ tone, title, children, className = '' }: KidHintProps): JSX.Element {
  const colors = TONE_CLASS[tone];
  return (
    <aside
      data-testid="kid-hint"
      role="note"
      aria-label={title}
      className={`flex items-start gap-3 rounded-xl border ${colors.border} ${colors.bg} px-4 py-3 ${className}`}
    >
      <span
        aria-hidden="true"
        className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg ${colors.iconBg} ${colors.text}`}
      >
        <Question size={18} weight="bold" />
      </span>
      <div className="flex flex-col gap-1 min-w-0">
        <p
          className={`font-display text-body-sm font-semibold ${colors.text}`}
          style={{ letterSpacing: '-0.005em' }}
        >
          {title}
        </p>
        <p className="text-body-sm text-paper-50 leading-snug">{children}</p>
      </div>
    </aside>
  );
}
